import React, {Dispatch, SetStateAction, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ICaptionData, CaptioningState, CaptioningTypes} from '../../../model/captionModels';
import captionApi from '../../../service/captionApi';
import {IWorkerState} from '../../../model/workerState';
import transcriptionApi from '../../../service/transcriptionApi';
import {TscButtonType, TscThemeNames, Glyphs, ControlButton} from '@techsmith/tsc-cloud-style-guide';
import {getCaptionEditorUrl} from '../../../util/UrlUtil';
import DataTableActionButtonGroup from '../DataTableActionButtonGroup';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {themeStore} from '../../../context/themeProvider';
import config from '../../../service/config';
import {UserPermissions} from '../../../constants/Constants';
import {mediaStore} from '../../../context/mediaProvider';
import {AlertModalPortal} from '../../util/ModalPortalHelper';

enum alertTypes {
   cancel,
   delete
}

export const CaptionsActionsBase: React.FC<ICaptionsActionsProps & IStateMappedProps> = ({captionData, setParentWorkerState, refreshCaptionData, theme, MediaId, Hash, XmpId}) => {
   const {t} = useTranslation();
   const [isAlertDisplayed, setAlertDisplayed] = useState(false);
   const [alertType, setAlertType] = useState(alertTypes.cancel);

   const onPublishClick = async () => {
      try {
         setParentWorkerState({isWorking: true, hasError: false});
         await captionApi.publishCaptionTrack(MediaId);
         window.location.reload();
      } catch {
         setParentWorkerState({isWorking: false, hasError: true});
      }
   };

   const onCancelClick = () => {
      setAlertType(alertTypes.cancel);
      setAlertDisplayed(true);
   };

   const onApproveClick = async () => {
      try {
         setParentWorkerState({isWorking: true, hasError: false});
         if (captionData.CaptioningType === CaptioningTypes.ThirdParty) {
            await transcriptionApi.approveThirdPartyTranscription(Hash);
         }
         refreshCaptionData();
      } catch {
         setParentWorkerState({isWorking: false, hasError: true});
      }
   };

   const cancelTranscription = async () => {
      try {
         setParentWorkerState({isWorking: true, hasError: false});
         if (captionData.CaptioningType === CaptioningTypes.ThirdParty) {
            await transcriptionApi.cancelThirdPartyTranscription(Hash);
         } else {
            await transcriptionApi.cancelAutomatedTranscription(Hash);
         }
         await captionApi.deleteCaptionTrack(MediaId);
         refreshCaptionData();
      } catch {
         setParentWorkerState({isWorking: false, hasError: true});
      }
   };

   const onDeleteClick = () => {
      setAlertType(alertTypes.delete);
      setAlertDisplayed(true);
   };

   const deleteCaptionTrack = async () => {
      try {
         setParentWorkerState({isWorking: true, hasError: false});
         await captionApi.deleteCaptionTrack(MediaId);
         if (captionData.CaptioningState === CaptioningState.Published || XmpId) {
            window.location.reload();
         } else {
            refreshCaptionData();
         }
      } catch {
         setParentWorkerState({isWorking: false, hasError: true});
      }
   };

   const cancelRemoveAlertModal: React.JSX.Element =
      (<AlertModalPortal
         title={t(alertType === alertTypes.cancel ?
            'accessibility.captions.alert.cancel.title' :
            'accessibility.captions.alert.delete.title')}
         onClose={() => setAlertDisplayed(false)}
         onConfirm={alertType === alertTypes.cancel ? cancelTranscription : deleteCaptionTrack}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t(alertType === alertTypes.cancel ?
            'accessibility.captions.alert.cancel.confirmButtonText' :
            'accessibility.captions.alert.delete.confirmButtonText')}
         onDismiss={() => setAlertDisplayed(false)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.back')}
         // eslint-disable-next-line react/no-children-prop
         children={t(alertType === alertTypes.cancel ?
            'accessibility.captions.alert.cancel.message' :
            'accessibility.captions.alert.delete.message')}
      />);

   const enablePublishOption: boolean = captionData.CaptioningState === CaptioningState.ReadyForEdit ||
      captionData.CaptioningState === CaptioningState.Editing;

   const enableEditOption: boolean = captionData.CaptioningState === CaptioningState.ReadyForEdit ||
      captionData.CaptioningState === CaptioningState.Editing ||
      captionData.CaptioningState === CaptioningState.Published;

   const showApproveOption: boolean = captionData.CaptioningState === CaptioningState.PendingApproval;
   const enableApproveOption: boolean = captionData.CaptioningState === CaptioningState.PendingApproval && config.userPermissions.includes(UserPermissions.captionAdmin);

   const enableCancelOption: boolean = captionData.CaptioningState === CaptioningState.Transcribing ||
      captionData.CaptioningState === CaptioningState.PendingApproval;

   return (
      <>
         <DataTableActionButtonGroup
            primaryButton={<ControlButton
               onClick={onPublishClick}
               buttonType={TscButtonType.primary}
               themeName={theme}
               glyph={<Glyphs.Publish16x16 />}
               title={t('accessibility.captions.publish')}
               disabled={!enablePublishOption}
               testId="captions-actions-publish-button"
               label={t('accessibility.captions.publish')}
            />}
            otherButtons={[
               showApproveOption ? <ControlButton
                  onClick={onApproveClick}
                  buttonType={TscButtonType.secondary}
                  themeName={theme}
                  glyph={<Glyphs.ThumbsUp16x16 />}
                  title={t('accessibility.captions.approve')}
                  width="2rem"
                  testId="captions-actions-approve-button"
                  key="captions-actions-approve-button"
                  disabled={!enableApproveOption}
               /> :
                  <ControlButton
                     href={getCaptionEditorUrl(Hash)}
                     buttonType={TscButtonType.secondary}
                     themeName={theme}
                     glyph={<Glyphs.Edit16x16 />}
                     title={t('accessibility.captions.edit')}
                     width="2rem"
                     disabled={!enableEditOption}
                     testId="captions-actions-edit-button"
                     key="captions-actions-edit-button"
                  />,
               enableCancelOption ?
                  <ControlButton
                     onClick={onCancelClick}
                     buttonType={TscButtonType.secondary}
                     themeName={theme}
                     glyph={<Glyphs.Close16x16 />}
                     title={t('accessibility.captions.cancel')}
                     width="2rem"
                     testId="captions-actions-cancel-button"
                     key="captions-actions-cancel-button"
                  /> :
                  <ControlButton
                     onClick={onDeleteClick}
                     buttonType={TscButtonType.secondary}
                     themeName={theme}
                     glyph={<Glyphs.Delete16x16 />}
                     title={t('accessibility.captions.delete')}
                     width="2rem"
                     testId="captions-actions-delete-button"
                     key="captions-actions-delete-button"
                  />]}
         />
         {isAlertDisplayed && cancelRemoveAlertModal}
      </>
   );
};

export interface ICaptionsActionsProps {
   captionData: ICaptionData;
   setParentWorkerState: Dispatch<SetStateAction<IWorkerState>>;
   refreshCaptionData: () => void;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   MediaId: string;
   Hash: string;
   XmpId: string;
}

const mapStatesToProps = (
   {captionData, setParentWorkerState, refreshCaptionData}: ICaptionsActionsProps,
   {theme}: Partial<IStateMappedProps>,
   {MediaId, Hash, XmpId}: Partial<IStateMappedProps>
): IStateMappedProps & ICaptionsActionsProps => ({captionData, setParentWorkerState, refreshCaptionData, theme, MediaId, Hash, XmpId});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(CaptionsActionsBase);
